import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import {FaAmbulance, FaWalking, FaBandAid, FaHospital, FaStethoscope, FaFlask, FaPills, FaEye, FaHeartbeat } from 'react-icons/fa';
import useScrollToHash from '../hooks/useScrollToHash';
import './OtherServicesPage.css';

const OtherServicesPage = () => {
  useScrollToHash(120);
  const otherServices = [
    {
      id: "physiotherapy",
      icon: <FaWalking />,
      title: "Physiotherapy & Rehabilitation",
      description: "Professional physiotherapy services to improve mobility, strength, and overall physical function at home.",
      features: [
        "Post-surgical rehabilitation",
        "Stroke and neurological recovery",
        "Joint and muscle pain management",
        "Mobility and balance training",
        "Sports injury recovery",
        "Geriatric physiotherapy"
      ]
    }, 
    {
      id: "wound-care",
      icon: <FaBandAid />,
      title: "Wound Care & Dressing",
      description: "Expert wound management and dressing services for faster healing and infection prevention.",
      features: [
        "Surgical wound care",
        "Diabetic wound management",
        "Pressure ulcer treatment",
        "Burn care and dressing",
        "Post-operative wound care",
        "Infection control and prevention"
      ]
    },
    {
      id: "post-operative-care",
      icon: <FaStethoscope />,
      title: "Post-Operative Care",
      description: "Specialized care following surgery to ensure smooth recovery and prevent complications.",
      features: [
        "Wound monitoring and care",
        "Pain management",
        "Medication administration",
        "Mobility assistance",
        "Nutrition support",
        "Follow-up coordination"
      ]
    },
    {
      id: "post-hospitalization",
      icon: <FaHospital />,
      title: "Post Hospitalization Care",
      description: "Transitional care after hospital discharge to ensure continued recovery at home.",
      features: [
        "Care plan implementation",
        "Vital signs monitoring",
        "Medication management",
        "Patient education",
        "Family training and support",
        "Doctor visit coordination"
      ]
    },
     {
      id: "chronic-disease",
      icon: <FaHeartbeat />,
      title: "Chronic Disease Management",
      description: "Comprehensive care for managing chronic conditions like diabetes, hypertension, and heart disease.",
      features: [
        "Diabetes monitoring and care",
        "Blood pressure management",
        "Heart disease monitoring",
        "Kidney disease support",
        "Respiratory condition care",
        "Medication compliance support"
      ]
    },
{
      id: "ambulance-emergency-service",
      icon: <FaAmbulance />,
      title: "Ambulance & Emergency Service",
      description: "Rapid ambulance response and emergency medical support for urgent healthcare needs, available 24/7.",
      features: [
        "24/7 ambulance availability",
        "Trained paramedic support",
        "Emergency medical care on-site",
        "Safe patient transport to hospital",
        "Advanced life support equipment",
        "Coordination with emergency departments"
      ]
    },
    {
      id: "pain-management",
      icon: <FaPills />,
      title: "Pain Management",
      description: "Comprehensive pain management services for acute and chronic pain conditions.",
      features: [
        "Chronic pain assessment",
        "Medication management",
        "Non-pharmacological interventions",
        "Cancer pain management",
        "Arthritis pain relief",
        "Post-surgical pain control"
      ]
    },
    {
      id: "eye-care",
      icon: <FaEye />,
      title: "Home Eye Care",
      description: "Professional eye care services delivered at home for patients with limited mobility.",
      features: [
        "Vision screening",
        "Cataract care and follow-up",
        "Diabetic eye care",
        "Glaucoma monitoring",
        "Eye infection treatment",
        "Post-operative eye care"
      ]
    }
  ];

  return (
    <div className="other-services-page">
      <section className="page-hero">
        <Container>
          <h1>Other Services</h1>
          <p>Supporting Your Loved Ones With Compassionate Care</p>
        </Container>
      </section>

      {/* Services Introduction */}
      <section className="services-intro">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2>Comprehensive Healthcare at Home</h2>
              <p>
                At Aroghya Radham, we go beyond basic care to provide specialized medical services 
                that complement our core offerings. From physiotherapy to diagnostic testing, we bring 
                professional healthcare directly to your doorstep.
              </p>
              <p>
                Each service is delivered by qualified professionals using modern equipment and 
                evidence-based practices to ensure the best outcomes for our patients. We understand 
                that every patient is unique, which is why our services are tailored to meet individual 
                needs and preferences.
              </p>
            </Col>
            <Col lg={6}>
              <img 
                src="https://images.unsplash.com/photo-1584515933487-779824d29309?w=800"
                alt="Healthcare Services"
                className="img-fluid rounded shadow-lg"
              />
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* Detailed Services Grid */}
      <section className="services-grid">
        <Container>
          <h2 className="text-center mb-5">Our Specialized Services</h2>
          <Row className="g-4">
            {otherServices.map((service, index) => (
              <Col key={index} lg={6} md={12} id={service.id}>
                <Card className="service-detail-card h-100">
                  <Card.Body>
                    <div className="service-card-icon">
                      {service.icon}
                    </div>
                    <h3>{service.title}</h3>
                    <p className="service-card-description">{service.description}</p>
                    <ul className="service-card-features">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>• {feature}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-other">
        <Container>
          <h2 className="text-center mb-5">Why Choose Our Services?</h2>
          <Row className="g-4">
            <Col md={4}>
              <div className="feature-box text-center">
                <div className="feature-icon">🏥</div>
                <h4>Expert Professionals</h4>
                <p>Certified specialists with extensive experience in their respective fields</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-box text-center">
                <div className="feature-icon">🏠</div>
                <h4>Home Convenience</h4>
                <p>Quality healthcare delivered in the comfort and safety of your home</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-box text-center">
                <div className="feature-icon">💰</div>
                <h4>Affordable Pricing</h4>
                <p>Competitive rates without compromising on quality of care</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default OtherServicesPage;

